import React, { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { SecretInput } from "src/components/ui/secret-input";
import { ErrorAlert, SuccessAlert } from "src/components/ui/status-alert";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";

import { DevinIntegrationFormUpdateIntegrationMutation } from "./__generated__/DevinIntegrationFormUpdateIntegrationMutation.graphql";
import { DevinIntegrationFormValidateIntegrationMutation } from "./__generated__/DevinIntegrationFormValidateIntegrationMutation.graphql";
import { IntegrationType } from "./__generated__/IntegrationsSection_organization.graphql";

const updateIntegrationMutation = graphql`
  mutation DevinIntegrationFormUpdateIntegrationMutation(
    $input: UpdateIntegrationInput!
  ) {
    updateIntegration(input: $input) {
      integration {
        id
        name
        integrationType
        credentials
      }
      error
    }
  }
`;

const validateIntegrationMutation = graphql`
  mutation DevinIntegrationFormValidateIntegrationMutation(
    $input: ValidateIntegrationInput!
  ) {
    validateIntegration(input: $input) {
      success
      error
    }
  }
`;

interface DevinIntegrationFormProps {
  onComplete: (success: boolean) => void;
  onCancel: () => void;
}

export default function DevinIntegrationForm({ onComplete, onCancel }: DevinIntegrationFormProps) {
  const [apiKey, setApiKey] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [validationSuccess, setValidationSuccess] = useState<string | null>(null);
  const [isValidated, setIsValidated] = useState(false);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  const [commitMutation, isMutationInFlight] = useMutation<DevinIntegrationFormUpdateIntegrationMutation>(
    updateIntegrationMutation,
  );
  const [commitValidateMutation, isValidationInFlight] = useMutation<DevinIntegrationFormValidateIntegrationMutation>(
    validateIntegrationMutation,
  );

  // Both fields are required at submit time — see validateInputs. If a future
  // edit-in-place flow is added (today the Manage dialog is display + disconnect
  // only), it MUST also send both fields. The backend's create_or_update merges,
  // but if the form ever submits only api_key, the silent fallback path would
  // preserve a stale organization_id and dispatch would break.
  const buildCredentials = () => ({
    api_key: apiKey.trim(),
    organization_id: organizationId.trim(),
  });

  const validateInputs = (): string | null => {
    if (!organizationId.trim()) return "Organization ID is required";
    if (!apiKey.trim()) return "API Key is required";
    return null;
  };

  const handleTest = () => {
    setFormError(null);
    setValidationSuccess(null);
    setIsValidated(false);

    const inputError = validateInputs();
    if (inputError) {
      setFormError(inputError);
      return;
    }

    const input = {
      integrationType: "DEVIN" as IntegrationType,
      credentials: JSON.stringify(buildCredentials()),
    };

    commitValidateMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          setFormError(errors.map((e) => e.message).join(", "));
          return;
        }
        if (!response.validateIntegration.success) {
          setFormError(response.validateIntegration.error || "Validation failed");
          return;
        }
        setValidationSuccess("Credentials validated successfully");
        setIsValidated(true);
      },
      onError: (error) => setFormError(error.message),
    });
  };

  const handleSave = () => {
    setFormError(null);
    setValidationSuccess(null);

    const inputError = validateInputs();
    if (inputError) {
      setFormError(inputError);
      return;
    }

    const input = {
      integrationType: "DEVIN" as IntegrationType,
      credentials: JSON.stringify(buildCredentials()),
    };

    commitMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          setFormError(errors.map((e) => e.message).join(", "));
          onComplete(false);
          return;
        }
        if (response.updateIntegration.error) {
          setFormError(response.updateIntegration.error);
          onComplete(false);
          return;
        }
        onComplete(true);
      },
      onError: (error) => {
        setFormError(error.message);
        onComplete(false);
      },
    });
  };

  const resetValidation = () => {
    setIsValidated(false);
    setValidationSuccess(null);
  };

  return (
    <div className="space-y-6">
      <div className="border-t border-border pt-4 space-y-6">
        <div className="space-y-2">
          <Label htmlFor="devin-org-id" className="text-muted-foreground">
            Organization ID
          </Label>
          <Tooltip
            content={!canModify ? "You don't have permission to modify integrations" : null}
            position="top"
            asChild
          >
            <span className="block w-full">
              <Input
                id="devin-org-id"
                value={organizationId}
                onChange={(e) => {
                  setOrganizationId(e.target.value);
                  resetValidation();
                }}
                placeholder="e.g. org_abc123def456"
                disabled={!canModify}
              />
            </span>
          </Tooltip>
          <p className="text-xs text-muted-foreground">
            Find this in app.devin.ai → Settings → Organization (it appears in the URL when you visit the org).
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="devin-api-key" className="text-muted-foreground">
            Service User API Key
          </Label>
          <Tooltip
            content={!canModify ? "You don't have permission to modify integrations" : null}
            position="top"
            asChild
          >
            <span className="block w-full">
              <SecretInput
                id="devin-api-key"
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  resetValidation();
                }}
                placeholder="cog_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                disabled={!canModify}
              />
            </span>
          </Tooltip>
        </div>
      </div>

      {formError && <ErrorAlert icon="none">{formError}</ErrorAlert>}
      {validationSuccess && <SuccessAlert icon="none">{validationSuccess}</SuccessAlert>}

      <div className="flex justify-end space-x-3 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={isMutationInFlight || isValidationInFlight}>
          Cancel
        </Button>
        <Tooltip
          content={!canModify ? "You don't have permission to connect integrations" : null}
          position="top"
        >
          <span className="inline-block">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={isMutationInFlight || isValidationInFlight || !canModify}
            >
              {isValidationInFlight ? "Testing..." : "Test Connection"}
            </Button>
          </span>
        </Tooltip>
        <div className="relative group">
          <Tooltip
            content={!canModify ? "You don't have permission to connect integrations" : null}
            position="top"
          >
            <span className="inline-block">
              <Button
                onClick={handleSave}
                disabled={!isValidated || isMutationInFlight || isValidationInFlight || !canModify}
              >
                {isMutationInFlight ? "Saving..." : "Connect"}
              </Button>
            </span>
          </Tooltip>
          {!isValidated && !isMutationInFlight && !isValidationInFlight && canModify && (
            <div className="absolute bottom-full mb-2 right-0 px-3 py-2 bg-card text-foreground text-sm rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap">
              Test the connection first.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
